import { Transform, type TransformOptions } from 'class-transformer';
import {
  type ValidationArguments,
  type ValidationOptions,
  ValidatorConstraint,
  type ValidatorConstraintInterface,
  matches,
  registerDecorator,
} from 'class-validator';
import _, { isArray, isEmpty, isNil, some } from 'lodash-es';

import { PhoneNumberValidation } from './PhoneNumberValidation';
import { HAS_ENG, HAS_NUMBER, HAS_SYMBOL, INVALID_PASSWORD_LANGUAGE } from './constants';

/**
 * 정규식들 중에서 하나라도 포함되면 false -> 에러 메세지를 반환합니다.
 * 배열에 적힌 모든 정규식들에 value가 포함되지 않을 때 true를 반환합니다.
 */
export const NotMatches = (patterns: RegExp | RegExp[], validationOptions?: ValidationOptions) =>
  function (object: object, propertyName: string) {
    registerDecorator({
      name: 'notMatches',
      target: object.constructor,
      propertyName: propertyName,
      options: validationOptions,
      validator: {
        validate(value: string) {
          if (isArray(patterns)) {
            return !some(patterns, (pattern) => matches(value, pattern));
          }

          return !matches(value, patterns);
        },
      },
    });
  };

export const Trim = (transformOptions?: TransformOptions): ((target: any, key: string) => void) =>
  Transform(({ value }: { value: string }) => {
    if (!_.isString(value)) {
      return value;
    }

    return value.trim();
  }, transformOptions);

/** @see {@link https://github.com/typestack/class-validator/issues/486} */
export function IsEqual(property: string, validationOptions?: ValidationOptions) {
  return (object: object, propertyName: string) => {
    registerDecorator({
      target: object.constructor,
      propertyName,
      options: validationOptions,
      constraints: [property],
      validator: IsEqualConstraint,
    });
  };
}

@ValidatorConstraint({ name: 'isEqual' })
export class IsEqualConstraint implements ValidatorConstraintInterface {
  validate(value: string, args: ValidationArguments) {
    const [relatedPropertyName] = args.constraints;
    const relatedValue = (args.object as any)[relatedPropertyName];

    return value === relatedValue;
  }
}

export function ContainsTwoOrMoreTypes(validationOptions?: ValidationOptions) {
  return (object: object, propertyName: string) => {
    registerDecorator({
      target: object.constructor,
      propertyName,
      options: validationOptions,
      validator: ContainsTwoOrMoreTypesConstraintConstraint,
    });
  };
}

@ValidatorConstraint({ name: 'containsTwoOrMoreTypes' })
export class ContainsTwoOrMoreTypesConstraintConstraint implements ValidatorConstraintInterface {
  validate(value: string) {
    if (ContainsTwoOrMoreTypesConstraintConstraint.isInvalidPasswordLanguage(value)) {
      return false;
    }

    return (
      [HAS_ENG, HAS_NUMBER, HAS_SYMBOL].map((regexp) => RegExp(regexp).test(value)).filter(Boolean)
        .length >= 2
    );
  }

  static isInvalidPasswordLanguage(value: string) {
    return !isNil(value) && !isEmpty(value) && RegExp(INVALID_PASSWORD_LANGUAGE).test(value);
  }
}

export function IsPhoneNumber(validationOptions?: ValidationOptions) {
  return function (object: NonNullable<unknown>, propertyName: string) {
    registerDecorator({
      name: 'IsPhoneNumber',
      target: object.constructor,
      propertyName: propertyName,
      options: validationOptions,
      validator: {
        validate(value: string, args: ValidationArguments) {
          const regex =
            PhoneNumberValidation.isPhoneNumberValidation(args.object) &&
            args.object.countryCode === '82'
              ? /^010\d{7,8}$/
              : /^\d{1,20}$/;

          return regex.test(value);
        },
        defaultMessage(args: ValidationArguments) {
          return `${args.property} must be a valid Korean phone number`;
        },
      },
    });
  };
}
